// Core
import React, {
  useCallback,
  useEffect,
  useMemo,
} from 'react';

// Libraries
import { useTranslation } from 'react-i18next';

// Components
import { AlertType } from '../../../../../layout/elements/Alert';
import { Drawer, DrawerSize } from '../../../../../layout/elements/Drawer';
import { AlertLine } from '../../../../../layout/icons';

// Drawers
import { Feeder } from './Feeder';
import { LidStatus } from './LidStatus';
import { LubricationUnit } from './LubricationUnit';
import { MotorControl } from './MotorControl';
import { MotorStatus } from './MotorStatus';

// View
import {
  VSIDrawerType,
  DrawersProps,
  DrawerContentProps,
} from './Drawers.types';
import {
  DrawersContainer,
  DrawerContentContainer,
} from './Drawers.style';

// Registry of drawer content components
const DRAWER_CONTENT_COMPONENTS: Record<VSIDrawerType, React.ComponentType<DrawerContentProps>> = {
  [VSIDrawerType.feeder]: Feeder,
  [VSIDrawerType.lidStatus]: LidStatus,
  [VSIDrawerType.lubricationUnit]: LubricationUnit,
  [VSIDrawerType.motorControl]: MotorControl,
  [VSIDrawerType.motorStatus]: MotorStatus,
};

// Drawer content test IDs
const DRAWER_CONTENT_TEST_IDS: Record<VSIDrawerType, string> = {
  [VSIDrawerType.feeder]: 'feeder-drawer',
  [VSIDrawerType.lidStatus]: 'lid-status-drawer',
  [VSIDrawerType.lubricationUnit]: 'lubrication-unit-drawer',
  [VSIDrawerType.motorControl]: 'motor-control-drawer',
  [VSIDrawerType.motorStatus]: 'motor-status-drawer',
};

// Drawer titles
const DRAWER_TITLES: Record<VSIDrawerType, string> = {
  [VSIDrawerType.feeder]: 'view.crusher.vsi.drawers.feeder.title',
  [VSIDrawerType.lidStatus]: 'view.crusher.vsi.drawers.lidStatus.title',
  [VSIDrawerType.lubricationUnit]: 'view.crusher.vsi.drawers.lubrication.title',
  [VSIDrawerType.motorControl]: 'view.crusher.vsi.drawers.motorControl.title',
  [VSIDrawerType.motorStatus]: 'view.crusher.vsi.drawers.motorStatus.title',
};

function Drawers({
  alertText,
  className,
  edgeStatus,
  equipmentUnitId,
  isDrawerOpen,
  lastLidOpenedAt,
  onCloseDrawer,
  selectedDrawer,
  testId,
  unitData,
}: DrawersProps): JSX.Element | null {
  const { t } = useTranslation();

  /* ***********************************************************************************************
  ************************************** COMPONENT HANDLING ****************************************
  *********************************************************************************************** */

  // Render the drawer content based on the selected drawer
  const renderDrawerContent = useCallback((): JSX.Element | null => {
    if (!selectedDrawer) return null;

    const ContentComponent = DRAWER_CONTENT_COMPONENTS[selectedDrawer];
    if (!ContentComponent) return null;

    const drawerContentTestId: string = DRAWER_CONTENT_TEST_IDS[selectedDrawer];

    return (
      <ContentComponent
        unitData={unitData}
        edgeStatus={edgeStatus}
        testId={drawerContentTestId}
        equipmentUnitId={equipmentUnitId}
        lastLidOpenedAt={lastLidOpenedAt}
      />
    );
  }, [
    selectedDrawer,
    unitData,
    edgeStatus,
    equipmentUnitId,
    lastLidOpenedAt,
  ]);

  // Auto-close Bin Feeder and Feeder Interlock drawer if condition becomes false
  useEffect((): void => {
    if (
      isDrawerOpen
      && selectedDrawer === VSIDrawerType.feeder
      && !unitData?.feedSuperiorEnable
    ) {
      onCloseDrawer();
    }
  }, [
    unitData?.feedSuperiorEnable,
    selectedDrawer,
    isDrawerOpen,
    onCloseDrawer,
  ]);

  // Drawer title based on the selected type
  const drawerTitle = useMemo((): string => {
    if (!selectedDrawer) return '';
    return t(DRAWER_TITLES[selectedDrawer]);
  }, [selectedDrawer, t]);

  // Don't render if no drawer is selected
  if (!selectedDrawer) return null;

  return (
    <Drawer
      testId={testId}
      className={className}
      title={drawerTitle}
      open={isDrawerOpen}
      onClose={onCloseDrawer}
      footer={false}
      size={DrawerSize.lg}
      alert={
        alertText
          ? {
            type: AlertType.warning,
            icon: <AlertLine />,
            description: alertText,
          }
          : false
      }
    >
      <DrawersContainer>
        <DrawerContentContainer>
          {renderDrawerContent()}
        </DrawerContentContainer>
      </DrawersContainer>
    </Drawer>
  );
}

export { Drawers };
